import React from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";

export const MAX_DELETE_STAIN_REASON_LENGTH = 500;

interface Props {
  opened: boolean;
  stainCode: string;
  slideLabel: string;
  caseNumber?: string;
  /** Staining has begun, so an order may already be at the instrument. */
  stainingStarted: boolean;
  reason: string;
  isDeleting: boolean;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteStainModal: React.FC<Props> = ({
  opened,
  stainCode,
  slideLabel,
  caseNumber,
  stainingStarted,
  reason,
  isDeleting,
  onReasonChange,
  onClose,
  onConfirm,
}) => {
  const handleReasonChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onReasonChange(event.currentTarget.value);
    },
    [onReasonChange]
  );

  const identity = React.useMemo(
    () => [caseNumber, slideLabel, stainCode].filter(Boolean).join(" · "),
    [caseNumber, slideLabel, stainCode]
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Delete stain"
      size="md"
      centered
      closeOnClickOutside={!isDeleting}
      closeOnEscape={!isDeleting}
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Text size="sm">
            This removes the slide from the case permanently. It cannot be
            undone.
          </Text>
          <Text size="sm" fw={600}>
            {identity}
          </Text>
        </Stack>

        {stainingStarted && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="orange"
            variant="light"
            title="Staining has already started"
            role="alert"
          >
            Any stainer order still queued will be cancelled, but the instrument
            may already hold this slide. Retrieve and discard the physical slide
            after deleting. If the slide was sent to an outside vendor, the
            send-out is cancelled but the glass stays off-site.
          </Alert>
        )}

        <Textarea
          label="Reason (optional)"
          description="Stored with the deletion record."
          placeholder="e.g. duplicate order, wrong block"
          value={reason}
          onChange={handleReasonChange}
          maxLength={MAX_DELETE_STAIN_REASON_LENGTH}
          autosize
          minRows={2}
          maxRows={5}
          disabled={isDeleting}
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="red" onClick={onConfirm} loading={isDeleting}>
            Delete stain
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
